import { Spacing, Typography } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { FC } from 'react';
import styled from 'styled-components';

import { workflowStepIcon } from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree';
import WorkflowRunStepStats from 'dashboard/components-lib/WorkflowRunStepStats';
import { createMockWorkflowRunStepLoop } from 'dashboard/lib/stubs/workflow-step';
import { WorkflowRunStep, WorkflowRunStepStatus } from 'dashboard/models/workflow-step';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';

const StyledCard = styled(Card)`
  padding: 0;
  margin: 0;
`;
const Title = styled(HStack)`
  ${Typography.TextMediumBold}
  padding: ${Spacing[2]};
`;

const Body = styled.div`
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  min-width: 300px;
  width: 100%;
  padding: ${Spacing[2]} ${Spacing[4]};
  margin: 0 -${Spacing[2]};
`;

interface WorkflowRunStepStatsCardProps {
  runStep?: WorkflowRunStep;
  stepName?: string;

  manualInput?: {
    name: string;
    status: WorkflowRunStepStatus;
    startTime: DateTime<true>;
    endTime: DateTime<true>;
  };
}

const WorkflowRunStepStatsCard: FC<WorkflowRunStepStatsCardProps> = ({
  stepName,
  runStep,
  manualInput = {
    name: 'Unknown',
    startTime: DateTime.now(),
    endTime: DateTime.now(),
    status: WorkflowRunStepStatus.Completed,
  },
}) => {
  const runStepToUse =
    runStep ||
    // Kind of weird to use a mock step in real code.
    // But this should only be until Workflow Blocks are implemented for loop/conditional/parallel children.
    // Then we would have real objects we can pass here.
    createMockWorkflowRunStepLoop({
      status: manualInput.status,
      'created-at': manualInput.startTime.toISO(),
      'updated-at': manualInput.endTime.toISO(),
    });

  return (
    <StyledCard>
      <Title justify='left'>
        {workflowStepIcon(runStepToUse.type, 18)}
        {stepName ?? manualInput.name}
        {[WorkflowRunStepStatus.Errored, WorkflowRunStepStatus.Failed].includes(
          runStepToUse.attributes.status,
        ) && (
          <Sticker aria-label='This step has an error' variant='danger'>
            Error
          </Sticker>
        )}
      </Title>
      <Body>
        <WorkflowRunStepStats step={runStepToUse} showHelpText={false} />
      </Body>
    </StyledCard>
  );
};

export default WorkflowRunStepStatsCard;
